import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { ClosableDialog } from 'components/controls/dialogs'
import { ActionButton, Button, CustomTooltip } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { activateMyPricingBotBeta, editUserSubEntity, previewPricingBotBeta } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PricingPlan, PricingPlanNode, PricingPlanNodeCondition } from 'shared'
import { useHandleSettingsChange } from '../../StockPricingPresenter'
import { FlattenedPricingPlanPresenter } from './FlattenedPricingPlanPresenter'

interface PricingPlanEditorDrawerProps {
  // The (flat) pricingPlans entry the new UI edits directly.
  plan: PricingPlan
  open: boolean
  onClose: () => void
  // Start with the title in inline-rename mode (used right after creating a
  // new plan, so the user lands focused on the name field).
  startInEditNameMode?: boolean
  // Try the new editor on a converted copy of the plan: edits stay in the drawer, nothing is saved
  preview?: boolean
}

export const PricingPlanEditorDrawer = ({
  plan,
  open,
  onClose,
  startInEditNameMode,
  preview,
}: PricingPlanEditorDrawerProps): JSX.Element => {
  const { t } = useTranslation()
  const handleChange = useHandleSettingsChange()
  const { fetchUser } = useUser()
  const { handle } = useErrorHandler()
  const { enqueueSnackbar } = useSnackbar()

  // Reloaded on every open, so a closed preview starts again from the saved plan
  const [previewPlan, setPreviewPlan] = useState<PricingPlan | null>(null)
  useEffect(() => {
    if (!preview || !open) return
    setPreviewPlan(null)
    previewPricingBotBeta(plan._id).then(setPreviewPlan).catch(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, open, plan._id])

  const currentPlan = preview ? previewPlan ?? plan : plan
  const savePlan = async (updatedPlan: PricingPlan): Promise<void> => {
    if (preview) setPreviewPlan(updatedPlan)
    else await handleChange(() => editUserSubEntity(updatedPlan, 'pricingPlans'))
  }

  const [activateDialogOpen, setActivateDialogOpen] = useState(false)
  const activateBeta = async (): Promise<void> => {
    setActivateDialogOpen(false)
    try {
      await activateMyPricingBotBeta()
      enqueueSnackbar(t('priceUpdates.betaActivated'), { variant: 'success' })
      onClose()
      await fetchUser()
    } catch (err) {
      handle(err)
    }
  }

  // Inline "rename" of the plan name in the drawer header.
  const [titleEditMode, setTitleEditMode] = useState<boolean>(startInEditNameMode ?? false)
  const [nameDraft, setNameDraft] = useState<string>(currentPlan.name)
  useEffect(() => {
    if (startInEditNameMode) {
      setTitleEditMode(true)
      setNameDraft(currentPlan.name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startInEditNameMode])

  // Dedupe Enter+blur which would otherwise fire saveName twice.
  const savingNameRef = useRef(false)
  const saveName = async (): Promise<void> => {
    if (savingNameRef.current) return
    savingNameRef.current = true
    try {
      const trimmed = nameDraft.trim()
      setTitleEditMode(false)
      if (!trimmed || trimmed === currentPlan.name) return
      await savePlan({ ...currentPlan, name: trimmed })
    } finally {
      savingNameRef.current = false
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ style: { width: '100vw' } }}>
      <div className="pricingPlanDrawerTitle">
        <div className="me-3">
          <CustomTooltip title={t('common.close').toString()}>
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </CustomTooltip>
        </div>
        {titleEditMode ? (
          <TextField
            autoFocus
            size="small"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void saveName()
              } else if (e.key === 'Escape') {
                setNameDraft(currentPlan.name)
                setTitleEditMode(false)
              }
            }}
            onBlur={() => {
              void saveName()
            }}
            placeholder={currentPlan.name}
            inputProps={{ 'aria-label': t('strategy.name') }}
            style={{ minWidth: 320 }}
          />
        ) : (
          <h5
            className="m-0 p-0"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setNameDraft(currentPlan.name)
              setTitleEditMode(true)
            }}
          >
            {currentPlan.name}
          </h5>
        )}
        {preview && (
          <Alert severity="warning" className="ms-3 py-0">
            {t('priceUpdates.betaPreviewNotice')}
          </Alert>
        )}
        {preview && (
          <div className="ms-auto">
            <ActionButton
              color="primary"
              onClick={() => setActivateDialogOpen(true)}
              text={t('priceUpdates.activateBeta')}
              icon="fa-light fa-rocket"
            />
          </div>
        )}
      </div>
      <Box flex={1} minHeight={0}>
        {open && (!preview || previewPlan) && (
          <FlattenedPricingPlanPresenter
            defaultMethod={currentPlan.defaultNode?.method ?? null}
            nodes={currentPlan.nodes ?? []}
            onConditionEdit={async (ownerNodeId, conditionId, newCondition) => {
              const updateNodes = (ns: PricingPlanNode[]): PricingPlanNode[] =>
                ns.map((n) => {
                  if (n.id === ownerNodeId) {
                    return {
                      ...n,
                      conditions: n.conditions.map((c) =>
                        c.id === conditionId ? (newCondition as PricingPlanNodeCondition) : c
                      ),
                    }
                  }
                  if (n.children?.length) {
                    return { ...n, children: updateNodes(n.children) }
                  }
                  return n
                })
              const updatedNodes = updateNodes(currentPlan.nodes ?? [])
              const updatedPlan: PricingPlan = { ...currentPlan, nodes: updatedNodes }
              await savePlan(updatedPlan)
            }}
            onConditionDelete={async (ownerNodeId, conditionId) => {
              // Removes the condition from its owner node. If it was the
              // node's last condition, removes the whole node.
              const updateNodes = (ns: PricingPlanNode[]): PricingPlanNode[] => {
                const out: PricingPlanNode[] = []
                for (const n of ns) {
                  if (n.id === ownerNodeId) {
                    const remaining = n.conditions.filter((c) => c.id !== conditionId)
                    if (remaining.length === 0) continue
                    out.push({ ...n, conditions: remaining })
                  } else if (n.children?.length) {
                    out.push({ ...n, children: updateNodes(n.children) })
                  } else {
                    out.push(n)
                  }
                }
                return out
              }
              const finalNodes = updateNodes(currentPlan.nodes ?? [])
              const updatedPlan: PricingPlan = { ...currentPlan, nodes: finalNodes }
              await savePlan(updatedPlan)
            }}
            onReorderNodes={async (newOrderedNodeIds) => {
              const byId = new Map((currentPlan.nodes ?? []).map((n) => [n.id, n]))
              const newNodes = newOrderedNodeIds
                .map((id) => byId.get(id))
                .filter((n): n is PricingPlanNode => Boolean(n))
              const updatedPlan: PricingPlan = { ...currentPlan, nodes: newNodes }
              await savePlan(updatedPlan)
            }}
            onCreateRule={async (newNode, insertIndex) => {
              const currentNodes = currentPlan.nodes ?? []
              const newNodes = [
                ...currentNodes.slice(0, insertIndex),
                newNode,
                ...currentNodes.slice(insertIndex),
              ]
              const updatedPlan: PricingPlan = { ...currentPlan, nodes: newNodes }
              await savePlan(updatedPlan)
            }}
            onConditionAdd={async (ownerNodeId, newCondition) => {
              const updateNodes = (ns: PricingPlanNode[]): PricingPlanNode[] =>
                ns.map((n) => {
                  if (n.id === ownerNodeId) {
                    return {
                      ...n,
                      conditions: [...n.conditions, newCondition],
                    }
                  }
                  if (n.children?.length) return { ...n, children: updateNodes(n.children) }
                  return n
                })
              const updatedNodes = updateNodes(currentPlan.nodes ?? [])
              const updatedPlan: PricingPlan = { ...currentPlan, nodes: updatedNodes }
              await savePlan(updatedPlan)
            }}
            onStrictnessChange={async (ownerNodeId, newStrictness) => {
              const updateNodes = (ns: PricingPlanNode[]): PricingPlanNode[] =>
                ns.map((n) => {
                  if (n.id === ownerNodeId) {
                    return { ...n, conditionStrictness: newStrictness }
                  }
                  if (n.children?.length) return { ...n, children: updateNodes(n.children) }
                  return n
                })
              const updatedPlan: PricingPlan = {
                ...currentPlan,
                nodes: updateNodes(currentPlan.nodes ?? []),
              }
              await savePlan(updatedPlan)
            }}
            onMethodChange={async (nodeId, newMethod) => {
              if (nodeId === 'default') {
                const updatedPlan: PricingPlan = {
                  ...currentPlan,
                  defaultNode: { ...currentPlan.defaultNode, method: newMethod },
                }
                await savePlan(updatedPlan)
                return
              }
              const updateNodes = (ns: PricingPlanNode[]): PricingPlanNode[] =>
                ns.map((n) => {
                  if (n.id === nodeId) return { ...n, method: newMethod }
                  if (n.children?.length) return { ...n, children: updateNodes(n.children) }
                  return n
                })
              const updatedPlan: PricingPlan = {
                ...currentPlan,
                nodes: updateNodes(currentPlan.nodes ?? []),
              }
              await savePlan(updatedPlan)
            }}
          />
        )}
      </Box>
      <ClosableDialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
        <DialogTitle disableTypography>
          <h3>{t('priceUpdates.activateBetaConfirmTitle')}</h3>
        </DialogTitle>
        <DialogContent>
          <p>{t('priceUpdates.activateBetaConfirmBody')}</p>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => setActivateDialogOpen(false)}>
            {t('common.close')}
          </Button>
          <Button variant="contained" color="primary" onClick={activateBeta}>
            {t('priceUpdates.activateBeta')}
          </Button>
        </DialogActions>
      </ClosableDialog>
    </Drawer>
  )
}
